/**
 * Vuex store module for Meilisearch client
 * Replaces the $root.algoliaClient antipattern
 */
import { MeiliSearch } from 'meilisearch';
import { USE_DEV_FIREBASE } from '../../../config';

const state = {
  client: null,
  indexes: {
    projects: null,
    users: null,
    organizations: null,
    chats: null,
  },
  config: null, // { host, apiKey }
};

const getters = {
  client: (state) => state.client,
  /**
   * @return {Index}
   */
  projectIndex: (state) => state.indexes.projects,
  userIndex: (state) => state.indexes.users,
  organizationIndex: (state) => state.indexes.organizations,
  chatIndex: (state) => state.indexes.chats,
  isInitialized: (state) => state.client !== null,
};

const actions = {
  /**
   * Initialize the Meilisearch client with credentials
   * @param commit
   * @param enterpriseID
   * @param searchKey
   */
  initialize({ commit }, { enterpriseID, searchKey }) {
    if (!enterpriseID || !searchKey) {
      console.error('Meilisearch: Missing enterpriseID and/or searchKey, client not initialized');
      commit('CLEAR_CLIENT');
      return;
    }

    const host = 'https://ms-440b1ffa2c29-38963.sfo.meilisearch.io';
    const client = new MeiliSearch({
      host,
      apiKey: searchKey,
    });

    commit('SET_CLIENT', client);
    commit('SET_CONFIG', { host, apiKey: searchKey });

    // Initialize indexes
    const inDev = USE_DEV_FIREBASE;

    const getIndexName = (indexType) => {
      // we use the same environment for dev and production since there's no simple free tier of Meilisearch (have to self-host)
      return inDev ? `dev-${enterpriseID}-${indexType}` : `${enterpriseID}-${indexType}`;
    }

    commit('SET_INDEX', { name: 'projects', index: client.index(getIndexName('projects')) });
    commit('SET_INDEX', { name: 'users', index: client.index(getIndexName('users')) });
    commit('SET_INDEX', { name: 'organizations', index: client.index(getIndexName('organizations')) });
    commit('SET_INDEX', { name: 'chats', index: client.index(getIndexName('chats')) });
  },

  /**
   * Clear the Meilisearch client (e.g., on logout)
   */
  clear({ commit }) {
    commit('CLEAR_CLIENT');
  },

  /**
   * Perform a multi-index search
   * @param {Array} queries - Array of search query objects
   * @returns {Promise} - Search results
   */
  async multiSearch({ state }, queries) {
    if (!state.client) {
      console.warn('Meilisearch: Client not initialized');
      return null;
    }
    return state.client.multiSearch({ queries });
  },

  async search({ state }, query) {
    if (!state.client) {
      console.warn('Meilisearch: Client not initialized');
      return null;
    }
    return state.client.search(query);
  },

  /**
   * Clear the client's cache
   */
  clearCache({ state }) {
    // Meilisearch JS client doesn't have a built-in cache like Algolia,
    // but we can reinitialize if needed
    if (state.config) {
      const { host, apiKey } = state.config;
      const client = new MeiliSearch({ host, apiKey });
      // Note: This creates a new client instance which effectively clears any internal state
      return client;
    }
  },
};

const mutations = {
  SET_CLIENT(state, client) {
    state.client = client;
  },
  SET_CONFIG(state, config) {
    state.config = config;
  },
  SET_INDEX(state, { name, index }) {
    state.indexes[name] = index;
  },
  CLEAR_CLIENT(state) {
    state.client = null;
    state.config = null;
    state.indexes = {
      projects: null,
      users: null,
      organizations: null,
      chats: null,
      jobs: null,
    };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
